import useSendMessage from "@web-src/features/chats/hooks/useSendMessage";
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from "react";
import { v4 as uuid4 } from "uuid";
import {
  ChatMessage,
  ChatMessagePayload,
  ChatMessagePayloadCallAction,
  ChatMessageType,
  ChatCallType,
  ScenarioCallType,
} from "@web-src/features/chats/types";
import AgoraRTC, { IAgoraRTCClient } from "agora-rtc-sdk-ng";
import { environment } from "@web-src/environments/environment";
import { PhoenixSocketContext } from "@web-src/features/chats/providers/PheonixSocket";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useAgoraTokenMutation } from "../callsApi";
import { ActiveCallProps, CallStage } from "../types";
import { useActiveCall } from "../hooks/useActiveCall";

type CallsContextType = {
  initiateCall?: (chatId: string, type: ChatMessageType) => void;
  joinCall?: (params: {
    channel: string;
    callType?: ChatCallType;
    type?: ScenarioCallType;
    to?: string;
    isIncomingCall?: boolean;
    conferenceMessage?: ChatMessagePayload["conference"];
  }) => void;
  leaveCall?: () => void;
  client?: IAgoraRTCClient;
  activeCall?: ReturnType<typeof useActiveCall>;
  incomingCall?: ChatMessage;
  callMsgId?: string;
  setIncomingCall?: React.Dispatch<
    React.SetStateAction<ChatMessage | undefined>
  >;
  videoDevices?: MediaDeviceInfo[];
  audioDevices?: MediaDeviceInfo[];
  playbackDevices?: MediaDeviceInfo[];
  activeCallProps?: ActiveCallProps;
  callStage?: CallStage;
  setCallStage?: React.Dispatch<React.SetStateAction<CallStage>>;
};

const client = AgoraRTC.createClient({
  mode: "rtc",
  codec: "vp8",
});

const CallsContext = createContext<CallsContextType>({});

const CallsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { me } = useMe();
  const sendMessage = useSendMessage();
  const [activeCallProps, setActiveCallProps] = useState<ActiveCallProps>();
  const activeCall = useActiveCall(client, activeCallProps);
  const [agoraToken] = useAgoraTokenMutation();
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>();
  const [playbackDevices, setPlaybackDevices] = useState<MediaDeviceInfo[]>();
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>();
  const [incomingCall, setIncomingCall] = useState<ChatMessage>();
  const [callMsgId, setCallMsgId] = useState<string>();
  const [callStage, setCallStage] = useState<CallStage>("off");
  const { incomingMessages$ } = useContext(PhoenixSocketContext);

  const { toast } = useToast({ variant: "web" });
  const { t } = useTranslations();
  useEffect(() => {
    if (activeCallProps?.channel === incomingCall?.payload.call_channel) {
      setIncomingCall(undefined);
    }
  }, [incomingCall, activeCallProps]);
  useEffect(() => {
    if (!activeCall) return;
    Promise.all([
      AgoraRTC.getMicrophones(),
      AgoraRTC.getPlaybackDevices(),
      AgoraRTC.getCameras(),
    ])
      .then(([microphones, newPlaybackDevices, newCameras]) => {
        const microphonesUnchanged =
          JSON.stringify(microphones) === JSON.stringify(audioDevices);
        const playbackDevicesUnchanged =
          JSON.stringify(newPlaybackDevices) ===
          JSON.stringify(playbackDevices);
        const camerasUnchanged =
          JSON.stringify(newCameras) === JSON.stringify(videoDevices);
        if (
          microphonesUnchanged &&
          playbackDevicesUnchanged &&
          camerasUnchanged
        ) {
          return;
        }
        setPlaybackDevices(newPlaybackDevices);
        setAudioDevices(microphones);
        setVideoDevices(newCameras);
      })
      .catch(() => {});
  }, [activeCall, audioDevices, playbackDevices, t, toast, videoDevices]);

  useEffect(() => {
    const incomingMessagesSubscription = incomingMessages$?.subscribe(
      (message) => {
        const isFromMe = message.from === me?.id;
        if (
          isFromMe &&
          message.payload.call_action ===
            ChatMessagePayloadCallAction.call_invite
        ) {
          setCallMsgId(message.msg_id);
        }
        if (
          message?.payload.call_action ===
            ChatMessagePayloadCallAction.call_invite &&
          message.from !== me?.id
        ) {
          setCallMsgId(message.msg_id);
          setIncomingCall(message);
        }
      }
    );
    return () => {
      incomingMessagesSubscription?.unsubscribe();
    };
  }, [incomingMessages$, me?.id]);

  const initiateCall = useCallback(
    async (chatId: string, chatMessageType: ChatMessageType) => {
      const channel = uuid4();
      const tokenData = await agoraToken({ channel });
      if (!("data" in tokenData)) {
        throw tokenData.error as Error;
      }
      await sendMessage({
        to: chatId,
        body: "",
        extraPayload: {
          conference: { channel },
        },
        type: chatMessageType,
      });
    },
    [agoraToken, sendMessage]
  );

  const joinCall = useCallback(
    async ({
      channel,
      callType,
      type,
      to,
      isIncomingCall,
      conferenceMessage,
    }: {
      channel: string;
      callType?: ChatCallType;
      type?: ScenarioCallType;
      to?: string;
      isIncomingCall?: boolean;
      conferenceMessage?: ChatMessagePayload["conference"];
    }) => {
      const tokenData = await agoraToken({ channel });
      if (!("data" in tokenData)) {
        throw tokenData.error as Error;
      }
      setActiveCallProps({
        token: tokenData.data,
        channel,
        appId: environment.agoraAppId,
        video: callType === ChatCallType.video,
        type: type || "conference",
        to: to || undefined,
        isIncomingCall,
        conferenceMessage: conferenceMessage || undefined,
      });
    },
    [agoraToken]
  );

  const leaveCall = useCallback(async () => {
    await activeCall?.leaveCall();
    setActiveCallProps(undefined);
    setAudioDevices(undefined);
    setPlaybackDevices(undefined);
    setVideoDevices(undefined);
  }, [activeCall]);

  const value: CallsContextType = useMemo(
    () => ({
      initiateCall,
      activeCall,
      incomingCall,
      callMsgId,
      setIncomingCall,
      joinCall,
      client,
      leaveCall,
      audioDevices,
      videoDevices,
      playbackDevices,
      activeCallProps,
      callStage,
      setCallStage,
    }),
    [
      initiateCall,
      activeCall,
      incomingCall,
      callMsgId,
      setIncomingCall,
      joinCall,
      leaveCall,
      audioDevices,
      videoDevices,
      playbackDevices,
      activeCallProps,
      callStage,
      setCallStage,
    ]
  );

  return (
    <CallsContext.Provider value={value}>{children}</CallsContext.Provider>
  );
};

export { CallsContext, CallsProvider };
